import React, { useState } from 'react';
import { api } from '../lib/api';

// Validate and sanitize social media link
const validateAndSanitizeSocialLink = (url, platform) => {
  if (!url || typeof url !== 'string') {
    throw new Error('URL tidak valid');
  }

  // Trim whitespace
  let sanitizedUrl = url.trim();

  // Remove query parameters first
  try {
    const urlObj = new URL(sanitizedUrl);
    sanitizedUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
  } catch (error) {
    throw new Error('Format URL tidak valid');
  }

  // Remove trailing slash after removing query parameters
  sanitizedUrl = sanitizedUrl.replace(/\/+$/, '');

  return sanitizedUrl;
};

const AddSocialLinkModal = ({ proposal, onClose, onSuccess }) => {
  const [socialMediaLink, setSocialMediaLink] = useState(proposal?.social_media_link || '');
  const [socialMediaPlatform, setSocialMediaPlatform] = useState(proposal?.social_media_platform || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLinkChange = (e) => {
    const value = e.target.value;
    setSocialMediaLink(value);
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate platform is selected
    if (!socialMediaPlatform) {
      setError('Pilih platform terlebih dahulu');
      return;
    }

    // Validate and sanitize link
    let validatedLink;
    try {
      validatedLink = validateAndSanitizeSocialLink(socialMediaLink, socialMediaPlatform);
      // Update the input with sanitized value
      setSocialMediaLink(validatedLink);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setLoading(true);

    try {
      await api.patch(`/api/proposals/${proposal.id}/social-link`, {
        social_media_link: validatedLink,
        social_media_platform: socialMediaPlatform,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Terjadi kesalahan saat menyimpan link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {proposal?.social_media_link ? 'Edit Social Media Link' : 'Add Social Media Link'}
          </h3>
          {proposal?.social_media_link && proposal?.status === 'monitored' && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
              <p className="text-sm font-medium">⚠️ Peringatan: Setelah mengedit link, status proposal akan kembali ke "Verified" dan harus di-verify lagi untuk masuk ke "Monitored".</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="socialPlatform" className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <select
                id="socialPlatform"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                value={socialMediaPlatform}
                onChange={(e) => setSocialMediaPlatform(e.target.value)}
              >
                <option value="">Pilih Platform</option>
                <option value="Instagram">Instagram</option>
                <option value="Tiktok">Tiktok</option>
                <option value="Youtube">Youtube</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="socialLink" className="block text-sm font-medium text-gray-700 mb-2">
                Social Media Post Link
              </label>
              <input
                type="url"
                id="socialLink"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                value={socialMediaLink}
                onChange={handleLinkChange}
                placeholder={socialMediaPlatform === 'Instagram' ? 'https://instagram.com/p/...' : socialMediaPlatform === 'Tiktok' ? 'https://www.tiktok.com/@username/video/... atau https://vt.tiktok.com/...' : socialMediaPlatform === 'Youtube' ? 'https://www.youtube.com/watch?v=... atau https://youtu.be/...' : 'https://...'}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSocialLinkModal;

